/**
 * @fileoverview React Query hooks for assignment management operations
 * @author Hospital Room Scheduler Team
 * @created 2025-01-08
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_CONFIG } from '../../api';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

/**
 * Template assignment structure for recurring assignments
 * @created 15-10-2025 16:40:00
 */
export interface RecurringAssignmentTemplate {
  id?: string;
  title: string;
  description?: string | undefined; // Explicitly allow undefined
  roomId: string;
  roomName?: string;
  userId: string;
  doctorName?: string;
  sessionTemplateId: string;
  sessionName?: string;
  date?: string; // Optional for template, required for backend
}

/**
 * Recurrence configuration structure
 * @created 15-10-2025 16:40:00
 */
export interface RecurrenceConfiguration {
  frequency: 'daily' | 'weekly' | 'monthly';
  interval: number;
  startDate?: string;
  endDate: string;
  daysOfWeek?: number[]; // 0=Sunday, 1=Monday, etc.
  skipHolidays?: boolean;
}

/**
 * Complete recurring assignment request structure
 * @created 15-10-2025 16:40:00
 */
export interface CreateRecurringAssignmentRequest {
  templateAssignment: RecurringAssignmentTemplate;
  recurrence: RecurrenceConfiguration;
}

/**
 * Recurring assignment creation response structure
 * @created 15-10-2025 16:40:00
 */
export interface RecurringAssignmentResponse {
  assignments: Array<{
    id: string;
    title: string;
    description?: string;
    roomId: string;
    userId: string;
    sessionTemplateId: string;
    date: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    published: boolean;
  }>;
  count: number;
  totalGenerated: number;
  skippedHolidays: number;
  skippedConflicts: number;
  conflicts: Array<{
    date: string;
    conflicts: Array<{
      code: string;
      message: string;
      suggestion?: any;
    }>;
  }>;
  dates: string[];
}

export interface Assignment {
  id: string;
  title: string;
  description?: string;
  roomId: string;
  userId: string;
  sessionTemplateId: string;
  date: string;
  status: 'CONFIRMED' | 'CANCELLED';
  conflictCode?: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
  room: {
    id: string;
    number: string;
    name: string;
    capacity: number;
    status: string;
    roomNumber: string; // Aliased field for backward compatibility
    floor: {
      id: string;
      name: string;
      building: {
        id: string;
        name: string;
      };
    };
  };
  users: {
    id: string;
    name: string;
    specialty?: string;
    department?: string;
  };
  doctor: {
    id: string;
    name: string;
    specialty?: string;
    department?: string;
    fullName: string; // Aliased field for backward compatibility
  };
  session_times: {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
    bufferMinutes: number;
  };
  roomSessionTime: {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
    bufferMinutes: number;
    label: string; // Aliased field for backward compatibility
    startMinutes: string; // Aliased field for backward compatibility
    endMinutes: string; // Aliased field for backward compatibility
  };
}

export interface CreateAssignmentData {
  title: string;
  description?: string;
  roomId: string;
  userId: string;
  sessionTemplateId: string;
  date: string;
}

export interface UpdateAssignmentData {
  title?: string;
  description?: string;
  userId?: string;
  sessionTemplateId?: string;
  status?: 'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED';
  note?: string;
  published?: boolean;
}

export interface AssignmentFilters {
  page?: number;
  limit?: number;
  roomId?: string;
  userId?: string;
  sessionTemplateId?: string;
  status?: string;
  date?: string;
  startDate?: string;
  endDate?: string;
}

export interface AssignmentStats {
  total: number;
  pending: number;
  confirmed: number;
  completed: number;
  cancelled: number;
  conflicts: number;
}

// =============================================================================
// API CLIENT FUNCTIONS
// =============================================================================

/**
 * Create a new assignment
 */
async function createAssignment(data: CreateAssignmentData): Promise<Assignment> {
  const response = await fetch(`${API_CONFIG.BASE_URL}/assignments`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // ENHANCED: 08-10-2025 22:15:00 - Preserve detailed conflict information
    const error = new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
    (error as any).conflictDetails = errorData.error?.details;
    (error as any).conflicts = errorData.error?.details?.conflicts;
    (error as any).suggestions = errorData.error?.details?.conflicts?.map((c: any) => c.suggestion).filter(Boolean);
    throw error;
  }

  const result = await response.json();
  if (!result.success || !result.data) {
    throw new Error(result.error?.message || 'Failed to create assignment');
  }

  return result.data;
}

/**
 * Get assignments with filtering
 */
async function getAssignments(filters: AssignmentFilters = {}): Promise<{
  assignments: Assignment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}> {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value.forEach(item => params.append(key, item));
      } else {
        params.append(key, value.toString());
      }
    }
  });

  const response = await fetch(`${API_CONFIG.BASE_URL}/assignments?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  if (!result.success || !result.data) {
    throw new Error(result.error?.message || 'Failed to fetch assignments');
  }

  return result.data;
}

/**
 * Get assignment by ID
 */
async function getAssignmentById(id: string): Promise<Assignment> {
  const response = await fetch(`${API_CONFIG.BASE_URL}/assignments/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  if (!result.success || !result.data) {
    throw new Error(result.error?.message || 'Failed to fetch assignment');
  }

  return result.data;
}

/**
 * Update assignment
 */
async function updateAssignment(id: string, data: UpdateAssignmentData): Promise<Assignment> {
  const response = await fetch(`${API_CONFIG.BASE_URL}/assignments/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  if (!result.success || !result.data) {
    throw new Error(result.error?.message || 'Failed to update assignment');
  }

  return result.data;
}

/**
 * Delete assignment
 */
async function deleteAssignment(id: string): Promise<void> {
  const response = await fetch(`${API_CONFIG.BASE_URL}/assignments/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error?.message || 'Failed to delete assignment');
  }
}

/**
 * Publish assignment
 */
async function publishAssignment(id: string): Promise<Assignment> {
  const response = await fetch(`${API_CONFIG.BASE_URL}/assignments/${id}/publish`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  if (!result.success || !result.data) {
    throw new Error(result.error?.message || 'Failed to publish assignment');
  }

  return result.data;
}

/**
 * Get assignment statistics
 */
async function getAssignmentStats(filters: AssignmentFilters = {}): Promise<AssignmentStats> {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, value.toString());
    }
  });

  const response = await fetch(`${API_CONFIG.BASE_URL}/assignments/stats?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  if (!result.success || !result.data) {
    throw new Error(result.error?.message || 'Failed to fetch assignment statistics');
  }

  // Map API response to expected interface
  const apiData = result.data;
  return {
    total: apiData.totalAssignments || 0,
    pending: apiData.unpublishedAssignments || 0,
    confirmed: apiData.publishedAssignments || 0,
    completed: 0, // Not provided by API
    cancelled: 0, // Not provided by API
    conflicts: apiData.conflictCount || 0,
  };
}

// =============================================================================
// REACT QUERY HOOKS
// =============================================================================

/**
 * Hook to get assignments with filtering
 */
export function useAssignments(filters: AssignmentFilters = {}) {
  return useQuery({
    queryKey: ['assignments', filters],
    queryFn: () => getAssignments(filters),
    retry: 2,
    retryDelay: 1000
  });
}

/**
 * Hook to get assignment by ID
 */
export function useAssignment(id: string) {
  return useQuery({
    queryKey: ['assignment', id],
    queryFn: () => getAssignmentById(id),
    enabled: !!id,
    retry: 2,
    retryDelay: 1000
  });
}

/**
 * Hook to get assignment statistics
 */
export function useAssignmentStats(filters: AssignmentFilters = {}) {
  return useQuery({
    queryKey: ['assignment-stats', filters],
    queryFn: () => getAssignmentStats(filters),
    retry: 2,
    retryDelay: 1000
  });
}

/**
 * Hook to create assignment
 */
export function useCreateAssignment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createAssignment,
    onSuccess: () => {
      // Invalidate and refetch assignments
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      queryClient.invalidateQueries({ queryKey: ['assignment-stats'] });
      queryClient.invalidateQueries({ queryKey: ['plannerGrid'] });
      queryClient.invalidateQueries({ queryKey: ['plannerFilters'] });
      queryClient.invalidateQueries({ queryKey: ['plannerDashboard'] });
    }
  });
}

/**
 * Hook to update assignment
 */
export function useUpdateAssignment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAssignmentData }) => 
      updateAssignment(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate and refetch assignments
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      queryClient.invalidateQueries({ queryKey: ['assignment', id] });
      queryClient.invalidateQueries({ queryKey: ['assignment-stats'] });
      queryClient.invalidateQueries({ queryKey: ['plannerGrid'] });
      queryClient.invalidateQueries({ queryKey: ['plannerFilters'] });
      queryClient.invalidateQueries({ queryKey: ['plannerDashboard'] });
    }
  });
}

/**
 * Hook to delete assignment
 */
export function useDeleteAssignment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteAssignment,
    onSuccess: () => {
      // Invalidate and refetch assignments
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      queryClient.invalidateQueries({ queryKey: ['assignment-stats'] });
      queryClient.invalidateQueries({ queryKey: ['plannerGrid'] });
      queryClient.invalidateQueries({ queryKey: ['plannerFilters'] });
      queryClient.invalidateQueries({ queryKey: ['plannerDashboard'] });
    }
  });
}

/**
 * Hook to publish assignment
 */
export function usePublishAssignment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: publishAssignment,
    onSuccess: () => {
      // Invalidate and refetch assignments
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      queryClient.invalidateQueries({ queryKey: ['assignment-stats'] });
      queryClient.invalidateQueries({ queryKey: ['plannerGrid'] });
      queryClient.invalidateQueries({ queryKey: ['plannerFilters'] });
      queryClient.invalidateQueries({ queryKey: ['plannerDashboard'] });
    }
  });
}

/**
 * Hook to bulk publish assignments
 */
export function useBulkPublishAssignments() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (publishData: {
      dateDay?: string;
      buildingId?: string;
      floorId?: string;
      roomIds?: string[];
    }) => {
      const response = await fetch(`${API_CONFIG.BASE_URL}/assignments/bulk-publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(publishData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success || !result.data) {
        throw new Error(result.error?.message || 'Failed to bulk publish assignments');
      }

      return result.data;
    },
    onSuccess: () => {
      // Invalidate and refetch assignments
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      queryClient.invalidateQueries({ queryKey: ['assignment-stats'] });
      queryClient.invalidateQueries({ queryKey: ['plannerGrid'] });
      queryClient.invalidateQueries({ queryKey: ['plannerFilters'] });
      queryClient.invalidateQueries({ queryKey: ['plannerDashboard'] });
    }
  });
}

/**
 * Hook to create recurring assignments with proper API integration
 * @created 15-10-2025 16:42:00
 */
export function useCreateRecurringAssignments() {
  const queryClient = useQueryClient();
  
  return useMutation<RecurringAssignmentResponse, Error, CreateRecurringAssignmentRequest>({
    mutationFn: async (recurrenceData: CreateRecurringAssignmentRequest) => {
      const response = await fetch(`${API_CONFIG.BASE_URL}/assignments/recurring`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(recurrenceData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success || !result.data) {
        throw new Error(result.error?.message || 'Failed to create recurring assignments');
      }

      // FIX: Return result.data directly (API client returns raw JSON, not Axios-style wrapped)
      // Backend returns: { success: true, data: { assignments: [...], count: 5, ... } }
      // We want: { assignments: [...], count: 5, ... }
      return result.data;
    },
    onSuccess: () => {
      // Invalidate and refetch assignments
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      queryClient.invalidateQueries({ queryKey: ['assignment-stats'] });
      queryClient.invalidateQueries({ queryKey: ['plannerGrid'] });
      queryClient.invalidateQueries({ queryKey: ['plannerFilters'] });
      queryClient.invalidateQueries({ queryKey: ['plannerDashboard'] });
    }
  });
}


/**
 * Hook to copy assignments
 */
export function useCopyAssignments() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (copyData: {
      sourceDate: string;
      targetDates: string[];
      roomIds?: string[];
      sessionTemplateIds?: string[];
      options?: {
        overwrite?: boolean;
        onlyVacancies?: boolean;
      };
    }) => {
      const response = await fetch(`${API_CONFIG.BASE_URL}/assignments/copy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("auth_token")}`
        },
        body: JSON.stringify(copyData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success || !result.data) {
        throw new Error(result.error?.message || "Failed to copy assignments");
      }

      return result.data;
    },
    onSuccess: () => {
      // Invalidate and refetch assignments
      queryClient.invalidateQueries({ queryKey: ["assignments"] });
      queryClient.invalidateQueries({ queryKey: ["assignment-stats"] });
      queryClient.invalidateQueries({ queryKey: ["plannerGrid"] });
    }
  });
}
